/**
 * KB Usage tab — read activity for the selected project.
 *
 * Two sections, top to bottom:
 *   1. TWO heatmaps of reads per day under one KB-type filter: your own, and
 *      everyone else's. The split is relative to whoever is logged in and is
 *      decided server-side, so this module never learns anybody's name. Agent
 *      and system reads count as the team, so the two grids sum to the total.
 *   2. The existing per-entry table (1d / 7d / 30d / total), sortable.
 *
 * The heatmaps used to be one per-person grid on their own `/reads` page, then a
 * single unsplit total. Folding them in here puts the activity and the per-entry
 * breakdown on one screen, which is where "what is being read, and by whom"
 * actually gets answered.
 */

import { fetchKBReadStatsWindow, type KBReadStatsWindow } from '../../api/kb.js'
import {
  fetchKBReadHeatmap,
  fetchKBReadDetail,
  type KBReadHeatmapRow,
  type KBReadDetailRow,
} from '../../api/insights.js'
import {
  WINDOW_DAYS,
  renderHeatmap,
  renderFilterChips,
  type HeatmapScope,
} from './kb_read_heatmap.js'
import { dispatchRoute } from '../../router.js'
import { renderTabBar } from './kb_list_render.js'
import { renderUsageTable, sortRows } from './kb_usage_table.js'

// ─── State ─────────────────────────────────────────────────────────

export let _view: 'list' | 'usage' = 'list'

let _usageData: KBReadStatsWindow[] = []
/**
 * Set when a request FAILED, as opposed to returning nothing. allSettled
 * swallowed the rejection and the table said "No per-entry read activity
 * recorded yet" -- a claim about the data, made when we had none. It sent us
 * looking for missing reads instead of a failing request.
 */
let _usageFailed = false
// One flag PER GRID. A single shared flag put a generic "could not load" banner
// above a grid full of valid data whenever only the other half failed.
let _mineFailed = false
let _teamFailed = false

// Heatmap state. Kept beside the table's because both render into #kb-content
// and every repaint rebuilds the whole tab.
// TWO grids: the viewer's own reads, and everybody else's. Held separately
// rather than filtered client-side, because the browser has no way to know which
// audit rows are the viewer's -- the server decides that from the credentials on
// the request.
let _heatmapMine: KBReadHeatmapRow[] = []
let _heatmapTeam: KBReadHeatmapRow[] = []
/** Which grid owns the open drawer. */
let _openScope: HeatmapScope | null = null
/**
 * Bumped on every request that can be superseded. Without it, picking `debt`
 * then `All types` lets the late `debt` rows land last and paint their numbers
 * under a chip reading aria-pressed=true; chip and grid then disagree for good.
 */
let _requestSeq = 0
let _typeFilter: string | null = null
/** Which day's drawer is open, as `YYYY-MM-DD`. */
let _openDay: string | null = null
let _detail: KBReadDetailRow[] = []
let _detailLoading = false
let _usageSort: { col: keyof KBReadStatsWindow; dir: 'asc' | 'desc' } = {
  col: 'last7d',
  dir: 'desc',
}

// ─── Tab switching ────────────────────────────────────────────────

/**
 * Return to the Entries tab. Called on every /kb route entry.
 *
 * `_view` is module-level and survives navigation, while renderKBList() renders
 * ONLY the tab bar when it is 'usage' — so leaving KB from the Usage tab and
 * coming back produced a page containing nothing but two tabs: no list, no
 * heatmap, no table. Resetting on entry matches how kb_list already clears its
 * filters and page (SL-US02), and makes /#/kb mean one thing.
 */
export function resetKbView(): void {
  _view = 'list'
  _openDay = null
  _openScope = null
  _detail = []
  _detailLoading = false
}

export function setKbView(view: string): void {
  _view = view === 'usage' ? 'usage' : 'list'
  if (_view === 'usage') {
    void loadKBUsage()
    return
  }
  // Going back to Entries re-dispatches the CURRENT route rather than assigning
  // location.hash.
  //
  // The hash is already '#/kb' while the Usage tab is showing — the tab is view
  // state, not a route — so assigning the same value fires no hashchange and
  // nothing re-rendered. Combined with the tab bar being missing from the Usage
  // markup, that left the tab with no way out of it at all.
  //
  // dispatchRoute() re-runs the '/kb' handler, which rebuilds the list view from
  // scratch. It is imported from the router, which knows nothing about KB views,
  // so this does not create a cycle with kb_list (which imports `_view` from
  // here).
  dispatchRoute()
}

// ─── Loading ───────────────────────────────────────────────────────

export async function loadKBUsage(): Promise<void> {
  const content = document.getElementById('kb-content')
  if (!content) return
  // The tab bar renders BEFORE the await, and again after: without it the
  // Usage tab painted over the only control that leads back to Entries.
  content.innerHTML = `${renderTabBar(_view)}<div class="loading"><div class="spinner"></div></div>`
  // Both in flight together — they are independent queries and the tab shows
  // nothing useful until both land. allSettled, not all: an empty heatmap must
  // not take the table down with it, or vice versa.
  const seq = ++_requestSeq
  const [mine, team, usage] = await Promise.allSettled([
    fetchKBReadHeatmap({
      days: WINDOW_DAYS,
      actor: 'mine',
      ...(_typeFilter ? { type: _typeFilter } : {}),
    }),
    fetchKBReadHeatmap({
      days: WINDOW_DAYS,
      actor: 'team',
      ...(_typeFilter ? { type: _typeFilter } : {}),
    }),
    fetchKBReadStatsWindow(),
  ])
  if (seq !== _requestSeq) return
  _heatmapMine = mine.status === 'fulfilled' ? mine.value : []
  _heatmapTeam = team.status === 'fulfilled' ? team.value : []
  _mineFailed = mine.status === 'rejected'
  _teamFailed = team.status === 'rejected'
  _usageData = usage.status === 'fulfilled' ? usage.value : []
  _usageFailed = usage.status === 'rejected'
  // Same reason as repaint(): the user may have left this tab while the two
  // queries were in flight.
  repaint()
}

/** Reload only the heatmap — used when the type filter changes. */
async function reloadHeatmap(): Promise<void> {
  const seq = ++_requestSeq
  const [mine, team] = await Promise.allSettled([
    fetchKBReadHeatmap({
      days: WINDOW_DAYS,
      actor: 'mine',
      ...(_typeFilter ? { type: _typeFilter } : {}),
    }),
    fetchKBReadHeatmap({
      days: WINDOW_DAYS,
      actor: 'team',
      ...(_typeFilter ? { type: _typeFilter } : {}),
    }),
  ])
  // Drop a response for a filter the user has already moved off.
  if (seq !== _requestSeq) return
  _heatmapMine = mine.status === 'fulfilled' ? mine.value : []
  _heatmapTeam = team.status === 'fulfilled' ? team.value : []
  _mineFailed = mine.status === 'rejected'
  _teamFailed = team.status === 'rejected'
  repaint()
}

function repaint(): void {
  // Only ever paint into the tab that is actually showing.
  //
  // Every write here is the tail of an async fetch, and the user can switch to
  // Entries while one is in flight. Without this guard a late detail response
  // would replace the freshly-rendered entry list with the Usage tab, leaving
  // the tab strip saying "Entries" over Usage content.
  if (_view !== 'usage') return
  const content = document.getElementById('kb-content')
  if (content) content.innerHTML = renderKBUsage()
}

// ─── Heatmap interaction ───────────────────────────────────────────

export function setReadsType(value: string): void {
  // action() data-arg values are always strings; 'null' clears the filter.
  const next = value === 'null' ? null : value
  if (_typeFilter === next) return
  _typeFilter = next
  // A filter change invalidates an open drawer — its contents were fetched for
  // the previous filter.
  _openDay = null
  _openScope = null
  _detail = []
  void reloadHeatmap()
}

/**
 * Open the evidence drawer for one cell. `arg` is `scope|day`.
 *
 * The scope travels with the day because the same date exists in both grids and
 * the drawer has to show the reads from the grid that was actually clicked.
 */
export function openReadsCell(arg: string): void {
  // Split on the FIRST separator, and treat a missing one as "no scope given"
  // rather than letting slice(0, -1) silently mangle the day.
  const sep = arg.indexOf('|')
  const scope: HeatmapScope = sep !== -1 && arg.slice(0, sep) === 'team' ? 'team' : 'mine'
  const day = sep === -1 ? arg : arg.slice(sep + 1)

  if (_openDay === day && _openScope === scope) {
    closeReadsCell()
    return
  }
  _openDay = day
  _openScope = scope
  _detail = []
  _detailLoading = true
  repaint()

  // EVERY branch is guarded on the cell still being the open one, not just the
  // success branch. An unguarded catch let a failed earlier request blank the
  // drawer the user had since opened, and an unguarded finally flashed
  // "no entries" over a drawer that was still loading.
  const mine = (): boolean => _openDay === day && _openScope === scope
  void fetchKBReadDetail({ day, actor: scope, ...(_typeFilter ? { type: _typeFilter } : {}) })
    .then((rows) => {
      if (mine()) _detail = rows
    })
    .catch(() => {
      if (mine()) _detail = []
    })
    .finally(() => {
      if (!mine()) return
      _detailLoading = false
      repaint()
    })
}

export function closeReadsCell(): void {
  _openDay = null
  _openScope = null
  _detail = []
  repaint()
}

// ─── Sorting ───────────────────────────────────────────────────────

export function setKbUsageSort(raw: string): void {
  const [col, dir] = raw.split(':')
  if (col && dir) {
    _usageSort = { col: col as keyof KBReadStatsWindow, dir: dir as 'asc' | 'desc' }
    const content = document.getElementById('kb-content')
    if (content) content.innerHTML = renderKBUsage()
  }
}

// ─── Rendering ─────────────────────────────────────────────────────

export function renderKBUsage(): string {
  // Every path that writes this into #kb-content must carry the tab bar with
  // it — the loading state, this render, and the sort re-render all replace the
  // whole container, and the bar is the only route back to Entries.
  const tabBar = renderTabBar(_view)

  // One set of chips above BOTH grids: the filter is a property of the
  // question, not of either half of the answer. Grids first, table second —
  // the aggregate is the headline, the per-entry breakdown sits under it.
  const shared = {
    openDay: _openDay,
    openScope: _openScope,
    detail: _detail,
    detailLoading: _detailLoading,
  }
  const heatmaps =
    renderFilterChips(_typeFilter) +
    renderHeatmap(_heatmapMine, { ...shared, scope: 'mine', failed: _mineFailed }) +
    renderHeatmap(_heatmapTeam, { ...shared, scope: 'team', failed: _teamFailed })

  return `${tabBar}${heatmaps}${renderUsageTable(sortRows(_usageData, _usageSort), _usageSort, _usageFailed)}`
}
